import { useState } from "react";
import { RANKS } from "../content/ranks";
import type { ImportChoice } from "../hooks/useStore";
import type { LocalChild } from "../save/store";
import { topRank } from "./WhosPlaying";
import "./Family.css";

interface Props {
  /** Children played on this device before signing in. */
  locals: LocalChild[];
  /** Children already in the family account. */
  family: LocalChild[];
  /** Still fetching the family from the server. */
  loading: boolean;
  onDone: (choices: ImportChoice[]) => void;
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** After signing in: add each child already on this device to the family, or to a child in it. */
export function ImportLocal({ locals, family, loading, onDone }: Props) {
  const [picks, setPicks] = useState<Record<string, string>>({});
  const pick = (c: LocalChild) =>
    picks[c.id] ?? family.find((f) => sameName(f.name, c.name))?.id ?? "new";

  const done = () =>
    onDone(
      locals.map((c) => {
        const p = pick(c);
        return p === "new" ? { childId: c.id, as: "new" } : { childId: c.id, as: "merge", into: p };
      }),
    );

  if (!locals.length)
    return (
      <section className="screen family">
        <h2 className="display family-title">You&apos;re signed in</h2>
        <div className="panel">
          <p>
            {loading
              ? "Getting your family…"
              : family.length
                ? `Found ${family.map((f) => f.name).join(" and ")}. Their progress is on this device now.`
                : "Next, add each child in Parents so they have their own profile."}
          </p>
          <button
            type="button"
            className="big display"
            onClick={() => onDone([])}
            disabled={loading}
          >
            Continue
          </button>
        </div>
      </section>
    );

  return (
    <section className="screen family">
      <h2 className="display family-title">Bring progress across</h2>
      <div className="panel">
        <p>
          {locals.length === 1 ? "This device has a player" : "This device has players"} from before
          you signed in. Choose where their progress goes. Nothing is lost: ranks and history are
          combined.
        </p>
        {loading && <p className="hint">Getting your family…</p>}
        {locals.map((c) => (
          <div key={c.id} className="import-row">
            <label htmlFor={`imp-${c.id}`}>
              {c.name || "Ninja"}: {RANKS[topRank(c)]!.name}, {c.base.history.length} games
            </label>
            <select
              id={`imp-${c.id}`}
              value={pick(c)}
              onChange={(e) => setPicks((p) => ({ ...p, [c.id]: e.target.value }))}
            >
              <option value="new">Add as a new child</option>
              {family.map((f) => (
                <option key={f.id} value={f.id}>
                  Add to {f.name}
                </option>
              ))}
            </select>
          </div>
        ))}
        <button type="button" className="big display" onClick={done} disabled={loading}>
          Done
        </button>
      </div>
    </section>
  );
}
